import React, { useContext, useEffect, useState } from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Card,
  Stack,
  Alert,
  Box,
  Divider,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik, FormikProvider, Form } from "formik";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi, fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import {
  teamPayoutScheduleUrl,
} from "src/react-query/endPoints";
import { payoutScheduleValidationSchema } from "src/util/validationSchemas";
import { payoutScheduleDefaultValues } from "../company/company.constants";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import { Loader } from "../Loader";
import { selector } from "src/react-query/select";
import { FREQUENCY_OPTIONS, DAY_OF_WEEK_OPTIONS, NTH_OCCURRENCE_OPTIONS } from "../company/company.constants";
import { is } from "src/util/is";
import { useParams } from "react-router-dom";
import { useGetDetail, useMutate } from "src/hooks/useFetch.hook";
import { Context } from "src/context";
import { toLocaleStringInTimezone } from "./teamTimezoneFormat";

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 28 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}));

const getDayName = (val) =>
  DAY_OF_WEEK_OPTIONS.find((d) => d.value === val)?.label || "";

const getOrdinalSuffix = (n) => {
  if (!n) return "";
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

const getScheduleDescription = (schedule) => {
  if (!schedule) return "No schedule configured";
  const { frequencyType, dayOfWeek, daysOfWeek, nthOccurrence, dayOfMonth, daysOfMonth } = schedule;

  switch (frequencyType) {
    case "WEEKLY": {
      const days = daysOfWeek && daysOfWeek.length > 0
        ? daysOfWeek.map(getDayName).join(", ")
        : getDayName(dayOfWeek);
      return `Every ${days}`;
    }
    case "MONTHLY_NTH_WEEKDAY": {
      const dayName = getDayName(dayOfWeek);
      const nthName = NTH_OCCURRENCE_OPTIONS.find((n) => n.value === nthOccurrence)?.label || "";
      return `${nthName} ${dayName} of every month`;
    }
    case "MONTHLY_FIXED_DATE": {
      const dates = daysOfMonth && daysOfMonth.length > 0
        ? daysOfMonth.map((d) => `${d}${getOrdinalSuffix(d)}`).join(", ")
        : `${dayOfMonth}${getOrdinalSuffix(dayOfMonth)}`;
      return `${dates} of every month`;
    }
    default:
      return "Unknown schedule";
  }
};

/**
 * Reads schedule data and returns normalized daysOfWeek array.
 * Falls back to wrapping legacy dayOfWeek in an array.
 */
const normalizeDaysOfWeek = (schedule) => {
  if (!schedule) return [5]; // default Friday
  if (schedule.daysOfWeek && schedule.daysOfWeek.length > 0) return schedule.daysOfWeek;
  if (schedule.dayOfWeek != null) return [schedule.dayOfWeek];
  return [];
};

/**
 * Reads schedule data and returns normalized daysOfMonth array.
 * Falls back to wrapping legacy dayOfMonth in an array.
 */
const normalizeDaysOfMonth = (schedule) => {
  if (!schedule) return [];
  if (schedule.daysOfMonth && schedule.daysOfMonth.length > 0) return schedule.daysOfMonth;
  if (schedule.dayOfMonth != null) return [schedule.dayOfMonth];
  return [];
};

const TeamPayoutScheduleTab = ({ teamId, parentCompanyId, isReadOnly }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { timezone } = useContext(Context);
  const {
    actions: { setAlert },
  } = alertSlice;

  const [inheritFromParent, setInheritFromParent] = useState(true);

  // Fetch effective schedule (resolves parent inheritance)

  const { data: effectiveData, isFetching } = useQuery(
    queryKeys.payoutSchedule(teamId),
    fetchWithError,
    { enabled: !is.empty(teamId) }
  );

  const { mutate, isLoading } = useMutate("payoutSchedule");

  // Form submit handler
  const onSubmit = async (scheduleData, { setSubmitting }) => {
    const mutateObject = {
      url: teamPayoutScheduleUrl(teamId),
      method: "POST",
    };

    if (effectiveData?.effectiveSchedule?.id && !is.nullOrUndefined(effectiveData?.effectiveSchedule?.id)) {
      mutateObject.method = "PUT";
      mutateObject.url = teamPayoutScheduleUrl(teamId);
    }
    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: scheduleData,
      },
      { onSuccess }
    );
    setSubmitting(false);
  };

  const onSuccess = () => console.log("Schedule updated successfully");

  const formik = useFormik({
    initialValues: payoutScheduleDefaultValues(),
    validationSchema: payoutScheduleValidationSchema,
    onSubmit,
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
    resetForm,
  } = formik;

  // Helper to build form values from a schedule object
  const buildFormValues = (schedule) => ({
    frequencyType: schedule.frequencyType || "WEEKLY",
    dayOfWeek: schedule.dayOfWeek,
    daysOfWeek: normalizeDaysOfWeek(schedule),
    nthOccurrence: schedule.nthOccurrence,
    dayOfMonth: schedule.dayOfMonth,
    daysOfMonth: normalizeDaysOfMonth(schedule),
    isActive: schedule.isActive ?? true,
    lastRunAt: schedule.lastRunAt,
    lastRunStatus: schedule.lastRunStatus,
  });

  // Populate form when data loads
  useEffect(() => {
    if (effectiveData) {
      const { teamSchedule, inherited, parentSchedule } = effectiveData;

      // Determine inherit state
      if (teamSchedule) {
        setInheritFromParent(teamSchedule.inheritFromParent ?? true);
      } else {
        setInheritFromParent(true);
      }

      // Populate form with team's own values (for override mode)
      const scheduleToUse = teamSchedule || parentSchedule;
      if (scheduleToUse) {
        resetForm({ values: buildFormValues(scheduleToUse) });
      }
    }
  }, [effectiveData, resetForm]);

  const handleInheritToggle = (e) => {
    const newValue = e.target.checked;
    setInheritFromParent(newValue);

    if (newValue && effectiveData?.parentSchedule) {
      resetForm({ values: buildFormValues(effectiveData.parentSchedule) });
    } else if (!newValue && effectiveData?.teamSchedule) {
      resetForm({ values: buildFormValues(effectiveData.teamSchedule) });
    }
  };

  const showDaysOfWeek = values.frequencyType === "WEEKLY";
  const showSingleDayOfWeek = values.frequencyType === "MONTHLY_NTH_WEEKDAY";
  const showNthOccurrence = values.frequencyType === "MONTHLY_NTH_WEEKDAY";
  const showDaysOfMonth = values.frequencyType === "MONTHLY_FIXED_DATE";

  const handleFrequencyChange = (e) => {
    const newType = e.target.value;
    setFieldValue("frequencyType", newType);
    if (newType === "WEEKLY") {
      setFieldValue("nthOccurrence", null);
      setFieldValue("dayOfMonth", null);
      setFieldValue("daysOfMonth", []);
      if (!values.daysOfWeek || values.daysOfWeek.length === 0) {
        setFieldValue("daysOfWeek", [5]); // default to Friday
      }
    } else if (newType === "MONTHLY_NTH_WEEKDAY") {
      setFieldValue("daysOfWeek", []);
      setFieldValue("dayOfMonth", null);
      setFieldValue("daysOfMonth", []);
    } else if (newType === "MONTHLY_FIXED_DATE") {
      setFieldValue("dayOfWeek", null);
      setFieldValue("daysOfWeek", []);
      setFieldValue("nthOccurrence", null);
      if (!values.daysOfMonth || values.daysOfMonth.length === 0) {
        setFieldValue("daysOfMonth", [1]); // default to 1st
      }
    }
  };

  const isFormDisabled = isReadOnly || inheritFromParent;
  const parentSchedule = effectiveData?.parentSchedule;

  if (isFetching) {
    return <Loader />;
  }

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Payout Schedule Configuration
          </Typography>

          {/* Inherit from parent toggle */}
          {parentCompanyId && (
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={inheritFromParent}
                    onChange={handleInheritToggle}
                    disabled={isReadOnly}
                  />
                }
                label="Inherit schedule from parent company"
              />
              {inheritFromParent && parentSchedule && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  This team uses the parent company's payout schedule:{" "}
                  <strong>{getScheduleDescription(parentSchedule)}</strong>
                </Alert>
              )}
              {inheritFromParent && !parentSchedule && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No payout schedule is configured for the parent company.
                  Please configure one at the company level or switch to a custom schedule.
                </Alert>
              )}
            </Box>
          )}

          {!inheritFromParent && parentCompanyId && (
            <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
              Custom schedule override enabled. This team will use its own
              schedule instead of the parent company's.
            </Alert>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Schedule fields */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="frequency-type-label">Frequency Type</InputLabel>
                <Select
                  required
                  labelId="frequency-type-label"
                  id="frequency-type"
                  label="Frequency Type"
                  value={values.frequencyType || ""}
                  onChange={handleFrequencyChange}
                  error={Boolean(touched.frequencyType && errors.frequencyType)}
                  disabled={isFormDisabled}
                >
                  {FREQUENCY_OPTIONS.map(({ value, label }) => (
                    <MenuItem value={value} key={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* WEEKLY: Multi-select days of week with chips */}
            {showDaysOfWeek && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="days-of-week-label">Days of Week</InputLabel>
                  <Select
                    multiple
                    labelId="days-of-week-label"
                    id="days-of-week"
                    label="Days of Week"
                    value={values.daysOfWeek || []}
                    onChange={(e) => setFieldValue("daysOfWeek", e.target.value)}
                    error={Boolean(touched.daysOfWeek && errors.daysOfWeek)}
                    disabled={isFormDisabled}
                    input={<OutlinedInput label="Days of Week" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected
                          .sort((a, b) => a - b)
                          .map((val) => (
                            <Chip key={val} label={getDayName(val)} size="small" />
                          ))}
                      </Box>
                    )}
                  >
                    {DAY_OF_WEEK_OPTIONS.map(({ value, label }) => (
                      <MenuItem value={value} key={value}>
                        <Checkbox checked={(values.daysOfWeek || []).indexOf(value) > -1} />
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* MONTHLY_NTH_WEEKDAY: Single day select (unchanged) */}
            {showSingleDayOfWeek && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="day-of-week-label">Day of Week</InputLabel>
                  <Select
                    required
                    labelId="day-of-week-label"
                    id="day-of-week"
                    label="Day of Week"
                    value={values.dayOfWeek ?? ""}
                    {...getFieldProps("dayOfWeek")}
                    error={Boolean(touched.dayOfWeek && errors.dayOfWeek)}
                    disabled={isFormDisabled}
                  >
                    {DAY_OF_WEEK_OPTIONS.map(({ value, label }) => (
                      <MenuItem value={value} key={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {showNthOccurrence && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="nth-occurrence-label">Occurrence</InputLabel>
                  <Select
                    required
                    labelId="nth-occurrence-label"
                    id="nth-occurrence"
                    label="Occurrence"
                    value={values.nthOccurrence ?? ""}
                    {...getFieldProps("nthOccurrence")}
                    error={Boolean(
                      touched.nthOccurrence && errors.nthOccurrence
                    )}
                    disabled={isFormDisabled}
                  >
                    {NTH_OCCURRENCE_OPTIONS.map(({ value, label }) => (
                      <MenuItem value={value} key={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* MONTHLY_FIXED_DATE: Multi-select days of month with chips */}
            {showDaysOfMonth && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="days-of-month-label">Days of Month</InputLabel>
                  <Select
                    multiple
                    labelId="days-of-month-label"
                    id="days-of-month"
                    label="Days of Month"
                    value={values.daysOfMonth || []}
                    onChange={(e) => setFieldValue("daysOfMonth", e.target.value)}
                    error={Boolean(touched.daysOfMonth && errors.daysOfMonth)}
                    disabled={isFormDisabled}
                    input={<OutlinedInput label="Days of Month" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected
                          .sort((a, b) => a - b)
                          .map((val) => (
                            <Chip
                              key={val}
                              label={`${val}${getOrdinalSuffix(val)}`}
                              size="small"
                            />
                          ))}
                      </Box>
                    )}
                  >
                    {DAY_OF_MONTH_OPTIONS.map(({ value, label }) => (
                      <MenuItem value={value} key={value}>
                        <Checkbox checked={(values.daysOfMonth || []).indexOf(value) > -1} />
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(values.isActive)}
                    onChange={(e) =>
                      setFieldValue("isActive", e.target.checked)
                    }
                    disabled={isFormDisabled}
                  />
                }
                label="Schedule Active"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Last Run At"
                value={toLocaleStringInTimezone(values.lastRunAt, timezone.timezone?.value)}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Last Run Status
              </Typography>
              {values.lastRunStatus === "SUCCESS" ? (
                <Chip label="SUCCESS" color="success" size="small" />
              ) : values.lastRunStatus === "FAILED" ? (
                <Chip label="FAILED" color="error" size="small" />
              ) : (
                <Chip label="N/A" size="small" />
              )}
            </Grid>
          </Grid>

          {/* Save button */}
          {!isReadOnly && (
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              sx={{ mt: 3 }}
            >
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
              >
                Save
              </LoadingButton>
            </Stack>
          )}

        </Card>
      </Form>
    </FormikProvider>
  );
};

export default TeamPayoutScheduleTab;
